import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap, faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import LocationMap from './LocationMap';

// Enhanced location input component with map
const LocationInput = ({ onLocationSelect, initialLocation, disabled }) => {
  const [latitude, setLatitude] = useState(initialLocation?.latitude || '');
  const [longitude, setLongitude] = useState(initialLocation?.longitude || '');
  const [selectedLocation, setSelectedLocation] = useState(initialLocation || null);
  const [showMap, setShowMap] = useState(false);

  const handleLocationSubmit = () => {
    if (latitude && longitude) {
      const location = {
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude)
      };
      setSelectedLocation(location);
      onLocationSelect(location);
    }
  };

  const handleMapLocationSelect = (location) => {
    setLatitude(location.latitude.toString());
    setLongitude(location.longitude.toString());
    setSelectedLocation(location);
    onLocationSelect(location);
  };

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation wird von diesem Browser nicht unterstützt.');
      return;
    }

    try {
      console.log('Requesting GPS permission...');
      
      const options = {
        enableHighAccuracy: true,
        timeout: 15000, // Increased timeout
        maximumAge: 30000 // Reduced max age for fresher data
      };

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
      });

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const accuracy = position.coords.accuracy;
      
      console.log('GPS success:', { lat, lng, accuracy });
      
      setLatitude(lat.toString());
      setLongitude(lng.toString());
      const location = { 
        id: crypto.randomUUID(), // CRITICAL: UUID for stable geo-identification
        latitude: lat, 
        longitude: lng, 
        accuracy: accuracy 
      };
      setSelectedLocation(location);
      onLocationSelect(location);
      
      alert(`Position erfolgreich ermittelt!\nGenauigkeit: ±${Math.round(accuracy)}m`);
      
    } catch (error) {
      console.error('GPS Error:', error);
      let errorMessage = 'GPS-Fehler: ';
      
      switch(error.code) {
        case 1: // PERMISSION_DENIED
          errorMessage += 'Standortzugriff wurde verweigert. Bitte erlauben Sie den Standortzugriff in den Browser-Einstellungen und laden Sie die Seite neu.';
          break;
        case 2: // POSITION_UNAVAILABLE
          errorMessage += 'Standortinformationen sind nicht verfügbar. Möglicherweise sind Sie in einem Gebäude oder die GPS-Signale sind schwach.';
          break;
        case 3: // TIMEOUT
          errorMessage += 'Die GPS-Anfrage ist abgelaufen. Versuchen Sie es erneut oder geben Sie die Position manuell ein.';
          break;
        default:
          errorMessage += `Ein unbekannter Fehler ist aufgetreten (Code: ${error.code || 'unbekannt'}).`;
          break;
      }
      
      // Add helpful instructions
      errorMessage += '\n\nTipp: Sie können die Position auch manuell eingeben oder die Karte verwenden.';
      
      alert(errorMessage);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label">Breitengrad</label>
          <input disabled={disabled}
            type="number"
            step="any"
            value={latitude}
            onChange={(e) => setLatitude(e.target.value)}
            placeholder="52.5200"
            className="form-input"
          />
        </div>
        <div>
          <label className="form-label">Längengrad</label>
          <input disabled={disabled}
            type="number"
            step="any"
            value={longitude}
            onChange={(e) => setLongitude(e.target.value)}
            placeholder="13.4050"
            className="form-input"
          />
        </div>
      </div>

      {!disabled && (<div className="flex space-x-2">
        <button
            type="button"
            onClick={getCurrentLocation}
            className="btn-passive"
            disabled={false}
        >
          <FontAwesomeIcon icon={faMapLocationDot} className="mr-2"/>
          Aktuelle Position
        </button>
        <button
            type="button"
            onClick={handleLocationSubmit}
            className="btn-primary"
        >
          Position bestätigen
        </button>
        <button
            type="button"
            onClick={() => setShowMap(!showMap)}
            className="btn-passive"
        >
          <FontAwesomeIcon icon={faMap} className="mr-2"/>
          {showMap ? 'Karte ausblenden' : 'Karte anzeigen'}
        </button>
      </div>)}
      
      {showMap && (
        <div className="mt-4">
          <label className="form-label">Auf Karte klicken um Position zu wählen:</label>
          <LocationMap 
            onLocationSelect={handleMapLocationSelect}
            selectedLocation={selectedLocation}
          />
        </div>
      )}
      
      {latitude && longitude && (
        <div className="text-sm text-gray-600">
          Position: {latitude}, {longitude}
        </div>
      )}
    </div>
  );
};

export default LocationInput;
